import { useEffect, useState, useRef } from "react";
import { Autocomplete, Chip } from "@mui/material";
import { Task } from "protogen/tasks_pb";
import {
  EditTaskRequest,
  ListTaskProjectsRequest,
} from "protogen/tasks_service_pb";
import {
  useUpdateTask,
  useListTaskProjects,
  useUpdateMemberTask,
} from "services/tasks";
import useIsMobile from "components/hooks/useIsMobile";
import TextField from "components/common/TextField";

export default ({
  initialTask,
  editable,
  onUpdate,
  isMember,
}: {
  initialTask: Task;
  editable: boolean;
  isMember: boolean;
  onUpdate?: (task: Task) => void;
}) => {
  const isMobile = useIsMobile();
  const useUpdateTaskFn = isMember ? useUpdateMemberTask : useUpdateTask;
  const { request: updateTaskRequest } = useUpdateTaskFn();
  const { request: listTaskProjectsRequest } = useListTaskProjects();
  const [task, setTask] = useState(initialTask);
  const [isEdit, setIsEdit] = useState(false);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [dynamicProjects, setDynamicProjects] = useState<string[]>([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const autocompleteRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTask(initialTask);
  }, [initialTask]);

  useEffect(() => {
    if (isEdit) {
      setAutocompleteOpen(true);
      // Lazily load projects when user starts editing
      if (!projectsLoaded) {
        loadProjects();
      }
      // Small delay to ensure the Autocomplete is fully rendered
      setTimeout(() => {
        if (autocompleteRef.current) {
          autocompleteRef.current.focus();
          // If there is a current project, select all text
          if (task?.project) {
            autocompleteRef.current.setSelectionRange(
              0,
              autocompleteRef.current.value.length,
            );
          }
        }
      }, 100);
    }
  }, [isEdit]);

  const loadProjects = async () => {
    const response = await listTaskProjectsRequest(
      new ListTaskProjectsRequest({
        familyRef: task?.familyRef || "",
      }),
    );
    if (!response) {
      console.error("Failed to load task projects");
      setProjectsLoaded(true); // Still mark as loaded to avoid retrying
      return;
    }
    // Combine community and family projects, removing duplicates
    const allProjects = Array.from(
      new Set([
        // Default projects
        "Meal planning",
        "Travel",
        "Party planning",
        "Home services",
        "Gifting guide",
        "Research",
        "Personal tasks",
        "Work",
        ...response?.projects,
      ]),
    );
    setDynamicProjects(allProjects);
    setProjectsLoaded(true);
  };

  const sx = {
    ...(!isMobile && {
      justifySelf: "end",
    }),
    ...(isMobile && {
      alignSelf: "end",
    }),
    flexShrink: 0,
  };

  const onChange = async (project: string | null) => {
    let finalProject = project;
    if (typeof project === "string" && project.startsWith('Add "')) {
      // Extract the user-typed value from 'Add "..."'
      finalProject = project.replace(/^Add \"/, "").replace(/\"$/, "");
    }

    const response = await updateTaskRequest(
      new EditTaskRequest({
        taskRef: task?.ref || "",
        updatedProject: finalProject || "",
      }),
    );
    if (response?.task) {
      setTask(response.task);
      onUpdate && onUpdate(response.task);
      setIsEdit(false);
      setAutocompleteOpen(false);
    }
  };

  const handleBlur = () => {
    // Close the autocomplete when user clicks away
    setAutocompleteOpen(false);
    setIsEdit(false);
  };

  if (!editable && !task?.project) {
    return null;
  } else if (!isEdit && task?.project) {
    return (
      <Chip
        size="medium"
        variant="outlined"
        onClick={() => setIsEdit(true)}
        label={task.project}
        sx={{
          paddingTop: "15px",
          paddingBottom: "15px",
          cursor: editable ? "pointer" : "default",
          "& .MuiChip-deleteIcon": { height: "16px", width: "16px" },
          ...sx,
        }}
      />
    );
  } else if (isEdit) {
    return (
      <Autocomplete
        freeSolo
        selectOnFocus
        size="small"
        options={dynamicProjects.sort((a, b) => a.localeCompare(b))}
        defaultValue={task?.project || undefined}
        onChange={(_, value) => onChange(value)}
        filterOptions={(options, params) => {
          const filtered = options.filter((option) =>
            option.toLowerCase().includes(params.inputValue.toLowerCase()),
          );

          const { inputValue } = params;
          const isExisting = options.some((option) => inputValue === option);
          if (inputValue !== "" && !isExisting) {
            filtered.push(`Add "${inputValue}"`);
          }

          return filtered;
        }}
        getOptionLabel={(option) => {
          if (typeof option === "string" && option.startsWith("Add ")) {
            return option.replace("Add ", "").replace(/"/g, "");
          }
          return option;
        }}
        renderOption={(props, option) => {
          const isAddOption =
            typeof option === "string" && option.startsWith("Add ");
          return (
            <li {...props} key={option}>
              {isAddOption && (
                <hr
                  style={{
                    margin: "8px 0",
                    border: "none",
                    borderTop: "1px solid #e0e0e0",
                  }}
                />
              )}
              {option}
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label=""
            placeholder="Project"
            size="small"
            inputRef={autocompleteRef}
          />
        )}
        sx={{
          ...sx,
          width: "160px",
          "& .MuiOutlinedInput-root": {
            minHeight: "fit-content",
          },
          "& input.MuiInputBase-input": {
            marginTop: "0",
          },
          "& .MuiInputBase-root": {
            paddingTop: "3.5px !important",
            paddingBottom: "3.5px !important",
          },
          "& .MuiInputLabel-root.MuiInputLabel-shrink": {
            left: "15px",
            top: "4px",
          },
          "label.MuiFormLabel-root": {
            top: "16px",
          },
        }}
        open={autocompleteOpen}
        onOpen={() => setAutocompleteOpen(true)}
        onClose={handleBlur}
        onBlur={handleBlur}
        autoFocus={isEdit}
      />
    );
  } else {
    return (
      <Chip
        onClick={() => setIsEdit(true)}
        size="medium"
        variant="outlined"
        label={"Add project"}
        sx={{
          cursor: "pointer",
          ...sx,
          paddingTop: "15px",
          paddingBottom: "15px",
        }}
      />
    );
  }
};
